import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../utils/api';
import { CATEGORIES } from '../constants/categories';
import {
  FaBriefcase,
  FaBuilding,
  FaMapMarkerAlt,
  FaDollarSign,
  FaClock,
  FaUsers,
  FaGraduationCap,
  FaFileAlt,
  FaCheckCircle,
  FaTimes,
  FaRedo,
  FaLink
} from 'react-icons/fa';
import FAQSection from '../components/FAQSection';

const PostJob = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingCompany, setCheckingCompany] = useState(true);
  const [userCompany, setUserCompany] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    requirements: '',
    responsibilities: '',
    location: {
      city: '',
      state: '',
      country: 'India'
    },
    category: '',
    employmentType: 'Full-time',
    workMode: 'On-site',
    salary: {
      min: '',
      max: '',
      currency: 'INR'
    },
    experience: {
      min: '',
      max: ''
    },
    skills: '',
    education: '',
    openings: 1,
    applicationDeadline: '',
    benefits: ''
  });

  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'];
  const workModes = ['On-site', 'Remote', 'Hybrid'];

  const checkSubscriptionAndCompany = useCallback(async () => {
    try {
      // Step 1: Check if user has a company
      let companyData = null;
      try {
        const companyRes = await API.get('/companies/my-company');
        companyData = companyRes.data;
      } catch (companyError) {
        // No company found
        toast.error('❌ Company Profile Required', {
          description: 'You need to create a company profile before posting jobs. Redirecting...'
        });
        setTimeout(() => {
          navigate('/create-company');
        }, 2500);
        setCheckingCompany(false);
        return;
      }

      // Step 2: Check if company is verified by admin
      if (companyData.documentVerification?.status !== 'verified') {
        console.log('Company verification status:', companyData.documentVerification?.status);
        toast.warning('⏳ Verification Pending', {
          description: 'Your company documents are under admin review. This usually takes 24-48 hours. Redirecting to your profile...'
        });
        setTimeout(() => {
          navigate('/company-profile');
        }, 3000);
        setCheckingCompany(false);
        return;
      }

      // Step 3: Company is verified, now check subscription
      try {
        const subRes = await API.get('/subscriptions/my-subscription');
        console.log('Active subscription found:', subRes.data);
        // Has subscription, continue
      } catch (subError) {
        // No subscription
        console.log('No active subscription found');
        toast.info('💳 Subscription Required', {
          description: 'Choose a plan to start posting jobs. Redirecting to plans...'
        });
        setTimeout(() => {
          navigate('/plans');
        }, 2500);
        setCheckingCompany(false);
        return;
      }

      // All checks passed - set company and continue
      setUserCompany(companyData);
      setFormData(prev => ({
        ...prev,
        company: companyData.name
      }));

    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setCheckingCompany(false);
    }
  }, [navigate, setUserCompany, setCheckingCompany, setFormData]);

  useEffect(() => {
    checkSubscriptionAndCompany();
  }, [checkSubscriptionAndCompany]);

  const checkVerificationStatus = async () => {
    setRefreshing(true);
    try {
      const { data } = await API.get('/companies/my-company/verification-status');
      console.log('Latest verification status:', data);

      if (data.isVerified) {
        toast.success('✅ Company Verified! You can now post jobs');
        // Refresh the page or re-check subscription
        setTimeout(() => {
          checkSubscriptionAndCompany();
        }, 1500);
      } else if (data.status === 'rejected') {
        toast.error('❌ Company Verification Rejected', {
          description: data.rejectionReason || 'Please contact support'
        });
      } else {
        toast.info('⏳ Still Under Review', {
          description: 'Your company documents are being reviewed. Please check back soon.'
        });
      }
    } catch (error) {
      toast.error('Failed to check status');
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.title.trim()) {
        toast.error('Job title is required');
        setLoading(false);
        return;
      }
      if (!formData.description.trim()) {
        toast.error('Job description is required');
        setLoading(false);
        return;
      }
      if (!formData.category) {
        toast.error('Category is required');
        setLoading(false);
        return;
      }
      if (formData.category === 'Other' && !customCategory.trim()) {
        toast.error('Please specify your category');
        setLoading(false);
        return;
      }
      if (!formData.location.city.trim()) {
        toast.error('Location city is required');
        setLoading(false);
        return;
      }
      if (!formData.salary.min || !formData.salary.max) {
        toast.error('Salary range is required');
        setLoading(false);
        return;
      }
      if ((!formData.experience.min && formData.experience.min !== 0) || !formData.experience.max) {
        toast.error('Experience range is required');
        setLoading(false);
        return;
      }
      if (!formData.skills.trim()) {
        toast.error('Skills are required');
        setLoading(false);
        return;
      }

      // Convert comma-separated skills to array
      const skillsArray = formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill);
      
      if (skillsArray.length === 0) {
        toast.error('At least one skill is required');
        setLoading(false);
        return;
      }
      
      // Convert benefits to array
      const benefitsArray = formData.benefits ? formData.benefits.split(',').map(benefit => benefit.trim()).filter(benefit => benefit) : [];

      // Convert requirements to array
      const requirementsArray = formData.requirements ? formData.requirements.split('\n').map(req => req.trim()).filter(req => req) : [];
      
      // Convert responsibilities to array  
      const responsibilitiesArray = formData.responsibilities ? formData.responsibilities.split('\n').map(resp => resp.trim()).filter(resp => resp) : [];

      const jobData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category === 'Other' ? customCategory : formData.category,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        location: {
          city: formData.location.city.trim(),
          state: formData.location.state?.trim() || '',
          country: formData.location.country || 'India'
        },
        salary: {
          min: Number(formData.salary.min),
          max: Number(formData.salary.max),
          currency: formData.salary.currency || 'INR'
        },
        experience: {
          min: Number(formData.experience.min),
          max: Number(formData.experience.max)
        },
        skills: skillsArray,
        requirements: requirementsArray,
        responsibilities: responsibilitiesArray,
        benefits: benefitsArray,
        openings: Number(formData.openings) || 1,
        applicationDeadline: formData.applicationDeadline || null,
        education: formData.education?.trim() || ''
      };

      console.log('Submitting job data:', jobData);

      const { data } = await API.post('/jobs', jobData);
      
      toast.success('Job posted successfully!');
      navigate(`/jobs/${data._id}`);
    } catch (error) {
      console.error('Error posting job:', error);
      
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Display validation errors
        error.response.data.errors.forEach(err => {
          toast.error(`${err.field}: ${err.message}`);
        });
      } else {
        toast.error(error.response?.data?.message || 'Failed to post job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  if (checkingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-primary/5 to-primary/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Verifying your company & subscription...</p>
          <button
            onClick={checkVerificationStatus}
            disabled={refreshing}
            className="mt-6 px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all duration-300 shadow-[0_8px_20px_rgb(0,194,171,0.2)] disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
          >
            <FaRedo className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Checking...' : 'Check Status Now'}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-20 pb-32 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-white opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-white opacity-10 blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-md mb-6 shadow-xl border border-white/30">
            <FaBriefcase className="text-white text-4xl" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Post a New Job
          </h1>
          <p className="text-lg text-teal-50 max-w-2xl mx-auto font-medium">
            Find the perfect candidate by providing detailed and clear requirements.
          </p>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="container mx-auto px-4 -mt-20 relative z-20 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Basic Information */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-shadow duration-300 hover:shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-4 border-b border-gray-100">
              <div className="p-3 bg-teal-50 rounded-xl text-[#00c2ab]">
                <FaFileAlt className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Basic Information</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Job Title <span className="text-[#00c2ab]">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Senior React Developer"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Company Name <span className="text-[#00c2ab]">*</span>
                </label>
                <div className="relative">
                  <FaBuilding className="absolute left-4 top-4 text-gray-400" />
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Your company name"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Category <span className="text-[#00c2ab]">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white appearance-none"
                  >
                    <option value="">Select Category</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Number of Openings <span className="text-[#00c2ab]">*</span>
                  </label>
                  <div className="relative">
                    <FaUsers className="absolute left-4 top-4 text-gray-400" />
                    <input
                      type="number"
                      name="openings"
                      value={formData.openings}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              {formData.category === 'Other' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Specify Your Category <span className="text-[#00c2ab]">*</span>
                  </label>
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="e.g., Hospitality, Agriculture, etc."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-shadow duration-300 hover:shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-4 border-b border-gray-100">
              <div className="p-3 bg-teal-50 rounded-xl text-[#00c2ab]">
                <FaBriefcase className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Job Details</h2>
            </div>
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Employment Type <span className="text-[#00c2ab]">*</span>
                  </label>
                  <select
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white appearance-none"
                  >
                    {employmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                    Work Mode <span className="text-[#00c2ab]">*</span>
                  </label>
                  <select
                    name="workMode"
                    value={formData.workMode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white appearance-none"
                  >
                    {workModes.map(mode => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Job Description <span className="text-[#00c2ab]">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Describe the role, what you're looking for, and what makes this opportunity exciting..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Requirements <span className="text-[#00c2ab]">*</span>
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="List the required qualifications, experience, and technical skills..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Responsibilities <span className="text-[#00c2ab]">*</span>
                </label>
                <textarea
                  name="responsibilities"
                  value={formData.responsibilities}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Outline the key responsibilities and day-to-day tasks..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Required Skills <span className="text-[#00c2ab]">*</span>
                </label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  required
                  placeholder="e.g., React, Node.js, MongoDB (comma separated)"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
                <p className="text-sm text-gray-400 mt-2 font-medium">Separate skills with commas</p>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-shadow duration-300 hover:shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-4 border-b border-gray-100">
              <div className="p-3 bg-teal-50 rounded-xl text-[#00c2ab]">
                <FaMapMarkerAlt className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Location</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  City <span className="text-[#00c2ab]">*</span>
                </label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Bangalore"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  State <span className="text-[#00c2ab]">*</span>
                </label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Karnataka"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Country <span className="text-[#00c2ab]">*</span>
                </label>
                <input
                  type="text"
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
              </div>
            </div>
          </div>

          {/* Compensation & Experience */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-shadow duration-300 hover:shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-4 border-b border-gray-100">
              <div className="p-3 bg-teal-50 rounded-xl text-[#00c2ab]">
                <FaDollarSign className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Compensation & Experience</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Salary Range (Annual) <span className="text-[#00c2ab]">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="number"
                      name="salary.min"
                      value={formData.salary.min}
                      onChange={handleChange}
                      required
                      placeholder="Minimum (e.g., 500000)"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      name="salary.max"
                      value={formData.salary.max}
                      onChange={handleChange}
                      required
                      placeholder="Maximum (e.g., 1000000)"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Experience Required (Years) <span className="text-[#00c2ab]">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="number"
                      name="experience.min"
                      value={formData.experience.min}
                      onChange={handleChange}
                      required
                      min="0"
                      placeholder="Minimum (e.g., 2)"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      name="experience.max"
                      value={formData.experience.max}
                      onChange={handleChange}
                      required
                      min="0"
                      placeholder="Maximum (e.g., 5)"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Education Requirement <span className="text-[#00c2ab]">*</span>
                </label>
                <div className="relative">
                  <FaGraduationCap className="absolute left-4 top-4 text-gray-400" />
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Bachelor's in Computer Science or equivalent"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 transition-shadow duration-300 hover:shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-4 border-b border-gray-100">
              <div className="p-3 bg-teal-50 rounded-xl text-[#00c2ab]">
                <FaCheckCircle className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Additional Information</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Benefits & Perks
                </label>
                <input
                  type="text"
                  name="benefits"
                  value={formData.benefits}
                  onChange={handleChange}
                  placeholder="e.g., Health Insurance, Flexible Hours, Remote Work (comma separated)"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                />
                <p className="text-sm text-gray-400 mt-2 font-medium">Separate benefits with commas</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Application Deadline <span className="text-[#00c2ab]">*</span>
                </label>
                <div className="relative">
                  <FaClock className="absolute left-4 top-4 text-gray-400" />
                  <input
                    type="date"
                    name="applicationDeadline"
                    value={formData.applicationDeadline}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm bg-gray-50 hover:bg-white focus:bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 flex flex-col sm:flex-row gap-4 justify-end mt-8">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="px-8 py-4 border-2 border-gray-200 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-10 py-4 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Posting Job...
                </>
              ) : (
                <>
                  <FaCheckCircle className="text-xl" />
                  Publish Job Post
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="container mx-auto px-4 max-w-4xl mt-12">
        <FAQSection 
          title="Post Job FAQ"
          subtitle="Common questions about posting and managing jobs."
          faqs={[
            { q: 'How much does it cost to post a job?', a: 'Your first job posting is free! After that, you can choose from our affordable pricing plans for additional postings and premium features.' },
            { q: 'How long will my job posting stay active?', a: 'Standard job postings remain active for 30 days. Premium postings can stay active for up to 60 days.' },
            { q: 'Can I edit my job posting after publishing?', a: 'Yes, you can edit your job posting details at any time from your Employer Dashboard.' },
            { q: 'How do I review applications?', a: 'All applications will be sent to your Employer Dashboard where you can review resumes, shortlist candidates, and contact them directly.' },
            { q: 'Can I hide my company name?', a: 'Yes, premium plans include a "Confidential Posting" option to hide your company name and logo from candidates.' }
          ]}
        />
      </div>
    </div>
  );
};

export default PostJob;
